import React, { useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { FileText } from 'lucide-react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { BrandedSpinner } from '@/components/common/BrandedLoading';
import { useQuery } from '@tanstack/react-query';
import { contentApi } from '@/services/adminApi';
import type { Content } from '@/types/admin';

const TermsPage: React.FC = () => {
  const TERMS_CONTENT_ID = 'CNT-five_ai_terms_conditions_V1';

  const fallback = useMemo(
    () => ({
      title: 'Terms of Service',
      description: undefined as string | undefined,
      lastUpdated: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
      sections: [
        {
          id: 'acceptance',
          title: '1. Acceptance of Terms',
          content:
            "By accessing and using Five AI's services, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.",
        },
        {
          id: 'service',
          title: '2. Description of Service',
          content:
            'Five AI provides an AI-powered career transformation platform designed to help individuals enhance their skills, advance their careers, and achieve professional growth. Our services include personalized learning paths, skill assessments, career guidance, and related educational content.',
        },
        {
          id: 'accounts',
          title: '3. User Accounts',
          content:
            'To access certain features of our service, you may be required to create an account. You are responsible for maintaining the confidentiality of your account credentials, all activities that occur under your account, providing accurate information, and notifying us immediately of any unauthorized use.',
        },
        {
          id: 'responsibilities',
          title: '4. User Responsibilities',
          content:
            "You agree to use Five AI's services only for lawful purposes and in a way that does not infringe the rights of others. You must not use the service for illegal purposes, attempt unauthorized access, interfere with service operation, transmit malware, or copy/modify/distribute content without authorization.",
        },
        {
          id: 'ip',
          title: '5. Intellectual Property',
          content:
            "All content, features, and functionality of Five AI's services are the exclusive property of Five AI and are protected by applicable intellectual property laws.",
        },
        {
          id: 'subscription',
          title: '6. Subscription and Payment',
          content:
            'Certain features may be provided through subscription plans. By subscribing, you agree to pay applicable fees, understand auto-renewal, and accept billing/cancellation policies.',
        },
        {
          id: 'privacy',
          title: '7. Privacy and Data Protection',
          content:
            'Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy. By using our services, you consent to the collection and use of your information as described there.',
        },
        {
          id: 'disclaimer',
          title: '8. Disclaimer of Warranties',
          content:
            'Five AI’s services are provided “as is” and “as available” without warranties of any kind. We do not guarantee uninterrupted or error-free service.',
        },
        {
          id: 'liability',
          title: '9. Limitation of Liability',
          content:
            'To the fullest extent permitted by law, Five AI shall not be liable for indirect, incidental, special, consequential, or punitive damages, or loss of profits, revenues, data, use, or goodwill.',
        },
        {
          id: 'modifications',
          title: '10. Modifications to Terms',
          content:
            'We may modify these Terms at any time by posting updated terms on this page. Continued use constitutes acceptance.',
        },
        {
          id: 'termination',
          title: '11. Termination',
          content:
            'We may terminate or suspend access immediately, without notice, for breach of these Terms. Upon termination, your right to use the service will cease.',
        },
        {
          id: 'law',
          title: '12. Governing Law',
          content:
            'These Terms are governed by applicable laws without regard to conflict of law provisions, and disputes shall be subject to competent courts.',
        },
        {
          id: 'contact',
          title: '13. Contact Information',
          content:
            'If you have questions about these Terms, please contact us through our support channels or visit our website for more information.',
        },
      ],
    }),
    []
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-terms', TERMS_CONTENT_ID],
    queryFn: () => contentApi.getByContentId(TERMS_CONTENT_ID),
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });

  const isRecord = (value: unknown): value is Record<string, unknown> =>
    !!value && typeof value === 'object' && !Array.isArray(value);

  const resolved = useMemo(() => {
    const content: Content | null = data ?? null;
    const metadata = content?.metadata;
    const termsContent = isRecord(metadata) && isRecord(metadata.termsContent) ? metadata.termsContent : null;
    const intro = termsContent && isRecord(termsContent.introduction) ? termsContent.introduction : null;
    const sectionsRaw = termsContent && Array.isArray(termsContent.sections) ? (termsContent.sections as unknown[]) : [];
    const footer = termsContent && isRecord(termsContent.footer) ? termsContent.footer : null;

    const lastUpdated =
      isRecord(metadata) && typeof metadata.lastUpdated === 'string'
        ? metadata.lastUpdated
        : content?.updatedAt
          ? new Date(content.updatedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
          : fallback.lastUpdated;

    const sections =
      sectionsRaw
        .map((s, idx) => {
          const sec = isRecord(s) ? s : null;
          const id = sec && typeof sec.id === 'string' ? sec.id : `section-${idx + 1}`;
          const title = sec && typeof sec.title === 'string' ? sec.title : `Section ${idx + 1}`;
          const contentText = sec && typeof sec.content === 'string' ? sec.content : '';
          return { id, title, content: contentText };
        })
        .filter((s) => s.title || s.content) || [];

    const headerTitle = content?.title || fallback.title;
    const headerDescription = content?.description || fallback.description;

    const introTitle = intro && typeof intro.title === 'string' ? intro.title : undefined;
    const introText = intro && typeof intro.text === 'string' ? intro.text : undefined;

    const footerTitle = footer && typeof footer.title === 'string' ? footer.title : undefined;
    const footerContent = footer && typeof footer.content === 'string' ? footer.content : undefined;

    return {
      isFallback: !content || !termsContent,
      headerTitle,
      headerDescription,
      lastUpdated,
      introTitle,
      introText,
      sections: sections.length > 0 ? sections : fallback.sections,
      footerTitle,
      footerContent,
    };
  }, [data, fallback]);

  // Enhance head values from content (title + description), fallback on hard-coded.
  useEffect(() => {
    document.title = resolved.headerTitle;
    const meta = document.querySelector('meta[name="description"]');
    if (meta && resolved.headerDescription) {
      meta.setAttribute('content', resolved.headerDescription);
    }
  }, [resolved.headerTitle, resolved.headerDescription]);

  const renderInline = (text: string) => {
    // Bold: **text**, Links: http(s)://...
    const boldParts = text.split(/\*\*(.*?)\*\*/g);
    return boldParts.map((part, idx) => {
      const isBold = idx % 2 === 1;
      const urlParts = part.split(/(https?:\/\/[^\s)]+)\b/g);
      const nodes = urlParts.map((p, j) => {
        const isUrl = /^https?:\/\//.test(p);
        if (!isUrl) return <React.Fragment key={`${idx}-${j}`}>{p}</React.Fragment>;
        return (
          <a
            key={`${idx}-${j}`}
            href={p}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline break-all"
          >
            {p}
          </a>
        );
      });
      return isBold ? <strong key={idx}>{nodes}</strong> : <span key={idx}>{nodes}</span>;
    });
  };

  return (
    <main id="main-content" className="min-h-screen bg-background" role="main" aria-label="Terms of Service">
      <Header />
      <div className="pt-16">
        {/* Hero — same pattern as Features */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.18),transparent_50%)] dark:bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.25),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_80%_20%,hsl(263_70%_50%/0.08),transparent)]" />
          <div className="container relative mx-auto px-4 py-24 sm:py-32">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8"
              >
                <FileText className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">Legal</span>
              </motion.div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1] tracking-tight">
                <span className="text-foreground">Terms of</span>
                <br />
                <span className="bg-gradient-to-r from-primary via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">
                  Service
                </span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-4">
                {resolved.headerDescription || 'Please read these terms carefully before using Five AI.'}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2 text-sm text-muted-foreground/80">
                <span>Last updated: {resolved.lastUpdated}</span>
                {isLoading && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs">
                    <BrandedSpinner sizeClassName="h-3 w-3" />
                    Loading…
                  </span>
                )}
                {isError && (
                  <span className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 text-xs">
                    Showing fallback
                  </span>
                )}
                {resolved.isFallback && !isLoading && !isError && (
                  <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs">
                    Default content
                  </span>
                )}
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-4 sm:py-8">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid gap-8 lg:grid-cols-[1fr_240px]">
              <div className="min-w-0">
                {(resolved.introTitle || resolved.introText) && (
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mb-10"
                  >
                    <Card className="rounded-2xl border-2 bg-gradient-to-br from-primary/5 to-violet-500/5 p-6 sm:p-8">
                      {resolved.introTitle && (
                        <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight mb-3">
                          {resolved.introTitle}
                        </h2>
                      )}
                      {resolved.introText && (
                        <div className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                          {renderInline(resolved.introText)}
                        </div>
                      )}
                    </Card>
                  </motion.div>
                )}

                <div className="space-y-10">
                  {resolved.sections.map((s, i) => (
                    <motion.section
                      key={s.id}
                      id={s.id}
                      className="scroll-mt-28"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: '-50px' }}
                      transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.3) }}
                    >
                      <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight mb-3">
                        {s.title}
                      </h2>
                      <div className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                        {renderInline(s.content)}
                      </div>
                    </motion.section>
                  ))}
                </div>

                {(resolved.footerTitle || resolved.footerContent) && (
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mt-10"
                  >
                    <Card className="rounded-2xl border-2 bg-muted/30 p-6 sm:p-8">
                      {resolved.footerTitle && (
                        <h2 className="text-xl font-bold text-foreground tracking-tight mb-3">
                          {resolved.footerTitle}
                        </h2>
                      )}
                      {resolved.footerContent && (
                        <div className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                          {renderInline(resolved.footerContent)}
                        </div>
                      )}
                    </Card>
                  </motion.div>
                )}
              </div>

              <aside className="hidden lg:block">
                <div className="sticky top-24">
                  <Card className="rounded-2xl border-2 p-6 bg-card">
                    <CardHeader className="p-0 mb-4">
                      <CardTitle className="text-base font-semibold text-foreground">On this page</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <nav className="space-y-2" aria-label="Terms sections">
                        {resolved.sections.map((s) => (
                          <a
                            key={s.id}
                            href={`#${s.id}`}
                            className="block text-sm text-muted-foreground hover:text-primary hover:underline transition-colors py-0.5"
                          >
                            {s.title}
                          </a>
                        ))}
                      </nav>
                    </CardContent>
                  </Card>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </main>
  );
};

export default TermsPage;
